// @ts-nocheck

/*
 * FIXME: I propose to skip typechecking this file for the moment because it
 * contains many google related stuff that would be difficult to type. So
 * better clean other more important types and come back to this when we turn
 * on typescript checking on CI.
 */
import { intersection } from 'lodash';
import React from 'react';

import { globalConfig } from '~src/context/config';

import { isStreetNumberReversed } from '~address-form/config/template-config';

import { TextInput, TextInputProps } from '../../ui/text-input';

const URL = `https://maps.googleapis.com/maps/api/js?key=${globalConfig.googleApiKey}&libraries=places`;

function convertGooglePlaceToAddress(place) {
  const address = {};

  let streetNumber = '';
  let street = '';
  let postalTown = undefined;

  place.address_components.forEach(component => {
    if (intersection(component.types, ['street_number']).length) {
      streetNumber = component.long_name;
    }
    if (intersection(component.types, ['route']).length) {
      street = component.long_name;
    }
    if (intersection(component.types, ['postal_code']).length) {
      address.postal_code = component.long_name.replace(/\W+/, '');
    }
    if (intersection(component.types, ['locality']).length) {
      address.city = component.long_name;
    }
    if (intersection(component.types, ['postal_town']).length) {
      postalTown = component.long_name;
    }
    if (intersection(component.types, ['administrative_area_level_1']).length) {
      address.region = component.short_name;
    }
    if (intersection(component.types, ['country']).length) {
      address.country = component.short_name;
    }
  });

  /**
   * If google return "locality" we use that for city, and if not then we use
   * the "postal_town". For countries that have different "locality" and
   * "postal_town", "locality" is more fine-grained and "postal_town" is
   * somewhat equal to "postal_code".
   */
  if (!address.city) {
    address.city = postalTown;
  }

  const streetLine = isStreetNumberReversed(address.country)
    ? `${streetNumber} ${street}`
    : `${street} ${streetNumber}`;

  return {
    ...address,
    address_lines: [streetLine],
  };
}

interface AutocompleteInputProps extends TextInputProps {
  country: string;
  formInstance: any;
}

export const AutocompleteInput = ({ country, formInstance, ...props }) => {
  const inputRef = React.useRef();

  const [preventEnter, setPreventEnter] = React.useState<boolean>();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (preventEnter) {
        event.preventDefault();
      }
    } else {
      setPreventEnter(true);
    }
  };

  const initAutocomplete = () => {
    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country },
      fields: ['address_components'],
    });

    const handleAutocompleteChange = () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) {
        return;
      }

      const address = convertGooglePlaceToAddress(place);
      /*
       * Set to empty string to clear the value. Setting to undefined has no effect.
       */
      formInstance.setFieldsValue(address ?? '');
    };

    autocomplete.addListener('place_changed', handleAutocompleteChange);

    google.maps.event.addDomListener(inputRef.current, 'keydown', handleKeyDown);
  };

  React.useEffect(() => {
    if (AutocompleteInput.loaded) {
      initAutocomplete();
      return;
    }

    const script = document.createElement('script');
    script.src = URL;
    script.onload = initAutocomplete;
    document.body.appendChild(script);
    AutocompleteInput.loaded = true;
  }, []);

  return <TextInput inputRef={inputRef} {...props} placeholder="" />;
};

interface AutocompleteInputFacadeProps extends AutocompleteInputProps {
  enableAutocomplete: boolean;
}

/*
 * Convenience helper to simplify typechecking and syntax in parent code.
 */
export const AutocompleteInputFacade = ({
  enableAutocomplete,
  country,
  formInstance,
  ...props
}) => {
  return enableAutocomplete ? (
    <AutocompleteInput country={country} formInstance={formInstance} {...props} />
  ) : (
    <TextInput autoComplete="street-address" {...props} />
  );
};
